// 設定 → 安全性 → API 金鑰（plan SPEC #api-keys-ui、AUTH-11）：金鑰代表本人，讓 Claude（kinio-vms-mcp）用我的權限查設備、事件、警報。
// 要已開兩步驟驗證才能建；建立 / 輪換 / 撤銷要 5 分鐘內驗證過（自動跳重新驗證）；金鑰只顯示一次。
// 輪換（plan SPEC #api-keys-lifecycle）：同設定建新的，舊的 24 小時後失效、期間只剩〔撤銷〕、不算在上限裡。
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../../components/ConfirmDialog";
import {
  ApiKeyScopeFields,
  confirmsFor,
  scopesConfirmed,
  type ScopeConfirms,
} from "./ApiKeyScopeFields";
import {
  API_KEY_CONSOLE_SCOPE,
  API_KEY_CONTROL_SCOPE,
  API_KEY_MAINTAIN_SCOPE,
  API_KEY_DEFAULT_SCOPES,
  API_KEY_MEDIA_SCOPES,
  SessionError,
  apiKeyActive,
  apiKeyRotated,
  createApiKey,
  listApiKeys,
  listPasskeys,
  mfaStatus,
  remoteMcpSetup,
  revokeAllApiKeys,
  revokeApiKey,
  rotateApiKey,
  updateApiKeyScopes,
  type ApiKeyInfo,
} from "../../lib/session";
import { dateStamp, stamp } from '../../lib/timefmt';
import { EmailVerifyPrompt, emailVerifyRequired } from "../../components/EmailVerifyPrompt";

const scopeKey = (s: string) => s.replace(/\./g, "_");

export function ApiKeysSection() {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["api-keys"], queryFn: () => listApiKeys() });
  const mfa = useQuery({
    queryKey: ["mfa-status"],
    queryFn: () => mfaStatus(),
  });
  const pk = useQuery({
    queryKey: ["passkeys"],
    queryFn: () => listPasskeys(),
  });
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(API_KEY_DEFAULT_SCOPES);
  const [ok, setOk] = useState<ScopeConfirms>(confirmsFor([]));
  /** 〔修改範圍〕中的金鑰（plan SPEC #api-keys-edit-scopes） */
  const [editing, setEditing] = useState<{
    id: string;
    name: string;
    scopes: string[];
    ok: ScopeConfirms;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [secret, setSecret] = useState<{
    name: string;
    secret: string;
    /** 輪換：舊金鑰在這之前照常能用 */
    oldUntil?: string;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（kq Q143）；處理了回 true。 */
  const [emailVerify, setEmailVerify] = useState<{ resend: boolean } | null>(null);
  const askEmailVerify = (e: unknown) => {
    const ev = emailVerifyRequired(e);
    if (ev) setEmailVerify(ev);
    return ev !== null;
  };
  const refresh = () => void qc.invalidateQueries({ queryKey: ["api-keys"] });
  const showErr = (e: unknown) =>
    askEmailVerify(e) ||
    setErr(
      e instanceof SessionError
        ? t(`apikeys.err_${e.code}`, {
            defaultValue: t("apikeys.err_default", { code: e.code }),
          })
        : t("common.error"),
    );
  const create = useMutation({
    mutationFn: () =>
      createApiKey({ name: name.trim(), scopes, expires_in_days: days }),
    onSuccess: (r) => {
      setSecret({ name: r.api_key.name, secret: r.secret });
      setErr(null);
      setName("");
      setScopes(API_KEY_DEFAULT_SCOPES);
      setOk(confirmsFor([]));
      refresh();
    },
    onError: showErr,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => (setErr(null), refresh()),
    onError: showErr,
  });
  const saveScopes = useMutation({
    mutationFn: (e: { id: string; scopes: string[] }) =>
      updateApiKeyScopes(e.id, e.scopes),
    onSuccess: () => {
      setEditing(null);
      setErr(null);
      setNotice(t("apikeys.scopes_saved"));
      refresh();
    },
    onError: showErr,
  });
  const rotate = useMutation({
    mutationFn: (id: string) => rotateApiKey(id),
    onSuccess: (r) => {
      setSecret({
        name: r.api_key.name,
        secret: r.secret,
        oldUntil: stamp(r.replaced.expires_at),
      });
      setErr(null);
      refresh();
    },
    onError: showErr,
  });
  const revokeAll = useMutation({
    mutationFn: () => revokeAllApiKeys(),
    onSuccess: () => (setErr(null), refresh()),
    onError: showErr,
  });
  const keys = q.data?.keys ?? [];
  const active = keys.filter((k) => apiKeyActive(k));
  const counted = active.filter((k) => !apiKeyRotated(k)); // 上限只算沒輪換的
  // 要已開兩步驟驗證（TOTP 或 passkey）才能建；還沒讀到時先當作有（伺服器端照樣擋 API_KEY_MFA_REQUIRED）
  const hasMfa =
    !mfa.data || mfa.data.totp_enabled || (pk.data?.length ?? 0) > 0;
  const canCreate =
    name.trim().length > 0 &&
    scopes.length > 0 &&
    scopesConfirmed(scopes, ok) &&
    !create.isPending;
  const setup = secret
    ? remoteMcpSetup(secret.secret) ||
      `claude mcp add kinio-vms -e KINIO_VMS_KEY=${secret.secret} -- kinio-vms-mcp`
    : "";

  return (
    <section className="card" aria-labelledby="apikeys-title">
      <div className="toolbar">
        <h2 className="section-title" id="apikeys-title">
          {t("apikeys.title")}{" "}
          <span className="muted small">
            {t("apikeys.count", {
              n: counted.length,
              limit: q.data?.limit ?? 5,
            })}
          </span>
        </h2>
        <div className="spacer" />
        {active.length > 0 && (
          <button
            className="btn btn-sm"
            disabled={revokeAll.isPending}
            onClick={() =>
              void ask({
                danger: true,
                title: t("apikeys.revoke_all_confirm", { n: active.length }),
                action: t("apikeys.revoke_all"),
              }).then((ok) => ok && revokeAll.mutate())
            }
          >
            {t("apikeys.revoke_all")}
          </button>
        )}
      </div>
      <p className="muted small">{t("apikeys.hint")}</p>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      {notice && (
        <p className="muted" role="status">
          {notice}
        </p>
      )}
      {editing && (
        <form
          className="card stack"
          aria-label={t("apikeys.edit_scopes_title", { name: editing.name })}
          onSubmit={(e) => {
            e.preventDefault();
            if (editing.scopes.length > 0 && scopesConfirmed(editing.scopes, editing.ok))
              saveScopes.mutate({ id: editing.id, scopes: editing.scopes });
          }}
        >
          <h3>{t("apikeys.edit_scopes_title", { name: editing.name })}</h3>
          <ApiKeyScopeFields
            scopes={editing.scopes}
            onChange={(next) => setEditing({ ...editing, scopes: next })}
            ok={editing.ok}
            onOk={(next) => setEditing({ ...editing, ok: next })}
          />
          <p className="muted small">{t("apikeys.edit_scopes_hint")}</p>
          <div className="toolbar">
            <button
              className="btn btn-primary"
              disabled={
                saveScopes.isPending ||
                editing.scopes.length === 0 ||
                !scopesConfirmed(editing.scopes, editing.ok)
              }
            >
              {t("apikeys.save_scopes")}
            </button>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
      {secret && (
        <div className="card review" role="status">
          <p>
            {secret.oldUntil
              ? t("apikeys.rotated_created", { name: secret.name })
              : t("apikeys.created", { name: secret.name })}
          </p>
          {secret.oldUntil && (
            <p className="small">
              {t("apikeys.rotated_old_until", { at: secret.oldUntil })}
            </p>
          )}
          <p>
            <code className="mono">{secret.secret}</code>{" "}
            <button
              className="btn btn-sm"
              onClick={() => void navigator.clipboard?.writeText(secret.secret)}
            >
              {t("apikeys.copy")}
            </button>
          </p>
          <p className="muted small">{t("apikeys.setup_hint")}</p>
          <p>
            <code className="mono small">{setup}</code>{" "}
            <button
              className="btn btn-sm"
              onClick={() => void navigator.clipboard?.writeText(setup)}
            >
              {t("apikeys.copy")}
            </button>
          </p>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setSecret(null)}
          >
            {t("apikeys.saved")}
          </button>
        </div>
      )}
      {keys.length > 0 ? (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t("apikeys.name")}</th>
                <th>{t("apikeys.scopes")}</th>
                <th>{t("apikeys.last_used")}</th>
                <th>{t("apikeys.expires")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k: ApiKeyInfo) => (
                <tr key={k.key_id}>
                  <td>
                    <b>{k.name}</b>
                    <br />
                    <code className="mono small">{k.prefix}…</code>
                  </td>
                  <td className="small">
                    {k.scopes.map((s) => (
                      <span
                        key={s}
                        className={
                          "badge " +
                          (API_KEY_MEDIA_SCOPES.includes(s) ||
                          s === "alarms.handle" ||
                          s === API_KEY_CONSOLE_SCOPE ||
                          s === API_KEY_CONTROL_SCOPE ||
                          s === API_KEY_MAINTAIN_SCOPE
                            ? "badge-warn"
                            : "")
                        }
                      >
                        {t(`apikeys.scope_${scopeKey(s)}`)}
                      </span>
                    ))}
                  </td>
                  <td className="small muted">
                    {k.last_used_at
                      ? stamp(k.last_used_at)
                      : t("apikeys.never_used")}
                    {k.last_used_ip && <> · {k.last_used_ip}</>}
                    {k.last_agent && <div>{k.last_agent}</div>}
                  </td>
                  <td className="small muted">
                    {apiKeyRotated(k) ? (
                      <>
                        <span className="badge badge-warn">
                          {t("apikeys.rotated")}
                        </span>{" "}
                        {t("apikeys.rotated_until", {
                          at: stamp(k.expires_at),
                        })}
                      </>
                    ) : (
                      dateStamp(k.expires_at)
                    )}
                  </td>
                  <td>
                    {apiKeyActive(k) ? (
                      <>
                        {!apiKeyRotated(k) && (
                          <button
                            className="btn btn-sm"
                            onClick={() => {
                              setNotice(null);
                              setEditing({
                                id: k.key_id,
                                name: k.name,
                                scopes: k.scopes,
                                ok: confirmsFor(k.scopes),
                              });
                            }}
                          >
                            {t("apikeys.edit_scopes")}
                          </button>
                        )}{" "}
                        {!apiKeyRotated(k) && (
                          <button
                            className="btn btn-sm"
                            disabled={rotate.isPending}
                            onClick={() =>
                              void ask({
                                danger: true,
                                title: t("apikeys.rotate_confirm", {
                                  name: k.name,
                                }),
                                action: t("apikeys.rotate"),
                              }).then((ok) => ok && rotate.mutate(k.key_id))
                            }
                          >
                            {t("apikeys.rotate")}
                          </button>
                        )}{" "}
                        <button
                          className="btn btn-sm"
                          disabled={revoke.isPending}
                          onClick={() =>
                            void ask({
                              danger: true,
                              title: t("apikeys.revoke_confirm", {
                                name: k.name,
                              }),
                              action: t("apikeys.revoke"),
                            }).then((ok) => ok && revoke.mutate(k.key_id))
                          }
                        >
                          {t("apikeys.revoke")}
                        </button>
                      </>
                    ) : (
                      <span className="muted small">
                        {k.revoked_at
                          ? t("apikeys.revoked")
                          : t("apikeys.expired")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !q.isLoading && <p className="muted">{t("apikeys.empty")}</p>
      )}
      {!hasMfa ? (
        <p className="muted">{t("apikeys.need_mfa")}</p>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate) create.mutate();
          }}
        >
          <h3>{t("apikeys.create")}</h3>
          <label>
            {t("apikeys.name")}{" "}
            <input
              value={name}
              maxLength={40}
              required
              placeholder={t("apikeys.name_placeholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <ApiKeyScopeFields
            scopes={scopes}
            onChange={setScopes}
            ok={ok}
            onOk={setOk}
          />
          <label>
            {t("apikeys.expires")}{" "}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {t("apikeys.days", { count: d })}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">{t("apikeys.limit_hint")}</p>
          <button className="btn btn-primary" disabled={!canCreate}>
            {t("apikeys.create")}
          </button>
        </form>
      )}
      {dialog}
      {emailVerify && <EmailVerifyPrompt resend={emailVerify.resend} onClose={() => setEmailVerify(null)} />}
    </section>
  );
}
